import { createLogger } from "@/logger";
import { PerfHub } from "./PerfHub";
import type { PerfRollup, PerfSink } from "./types";

const log = createLogger("telemetry");

const DEFAULT_INTERVAL_MS = 30_000;

export interface PerfReporterOptions {
  /** Cadence for periodic rollups. */
  intervalMs?: number;
  /** Per-flush context (e.g. current workspaceId) attached to each rollup. */
  getContext?: () => Record<string, string | undefined>;
  /** Injectable scheduler so tests can run deterministically; defaults to requestIdleCallback/timeout. */
  schedule?: (cb: () => void) => void;
}

/**
 * Drives periodic and lifecycle-triggered perf rollups to a {@link PerfSink}. Designed to be cheap
 * and bounded:
 *
 * - Interval flushes run on idle time (`requestIdleCallback`) so they never contend with rendering.
 * - The `visibilitychange→hidden` / `pagehide` flush builds the rollup synchronously and ships it via
 *   the sink's beacon path, the only reliable way to capture a session's final numbers on unload.
 * - When the sink is inactive (the default no-op), {@link start} is a no-op: nothing is scheduled and
 *   nothing is computed, so a build without an external destination wired carries no runtime cost.
 */
export class PerfReporter {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly intervalMs: number;
  private readonly getContext?: () => Record<string, string | undefined>;
  private readonly schedule: (cb: () => void) => void;
  private readonly onVisibilityChange = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.flushBeacon("visibility-hidden");
    }
  };
  private readonly onPageHide = () => this.flushBeacon("pagehide");

  public constructor(
    private readonly sink: PerfSink,
    options: PerfReporterOptions = {},
  ) {
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.getContext = options.getContext;
    this.schedule = options.schedule ?? defaultSchedule;
  }

  public start(): void {
    if (!this.sink.isActive) return;
    if (this.intervalId) return;

    this.intervalId = setInterval(() => this.flush("interval"), this.intervalMs);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.onPageHide);
    }
    log.debug("[perf] reporter started", { sink: this.sink.name, intervalMs: this.intervalMs });
  }

  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.onPageHide);
    }
  }

  /** Force an immediate (idle-scheduled) flush, e.g. when a cold boot completes. */
  public flushNow(reason: string): void {
    if (!this.sink.isActive) return;
    this.flush(reason);
  }

  private flush(reason: string): void {
    this.schedule(() => {
      try {
        this.sink.send(PerfHub.rollup(reason, this.getContext?.()));
        // Reset after a successful send so each rollup is a self-contained per-flush window rather
        // than an ever-growing cumulative total (a keep-on-error path would fold this window into
        // the next one instead of dropping it).
        PerfHub.reset();
      } catch (error) {
        log.warn("[perf] reporter flush failed", { reason, error });
      }
    });
  }

  private flushBeacon(reason: string): void {
    // Synchronous: page teardown gives no time for idle callbacks or awaits.
    try {
      const rollup = PerfHub.rollup(reason, this.getContext?.());
      // A `visibilitychange→hidden` beacon resets the collectors, so a following `pagehide` beacon
      // would otherwise ship an empty window. Skip empty rollups to keep those noise rows out.
      if (isEmptyRollup(rollup)) return;
      this.sink.send(rollup, { beacon: true });
      PerfHub.reset();
    } catch {
      // Never let a teardown flush throw and disrupt unload.
    }
  }
}

/** True when a rollup carries no activity — nothing was measured in the window since the last flush. */
function isEmptyRollup(rollup: PerfRollup): boolean {
  return (
    rollup.bootstrapFullMs === null &&
    rollup.bootstrapLoadMs === null &&
    rollup.query.executions === 0 &&
    rollup.render.count === 0 &&
    rollup.network.count === 0 &&
    Object.keys(rollup.actions).length === 0
  );
}

function defaultSchedule(cb: () => void): void {
  const ric = (
    globalThis as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;
  // Bound the idle wait: under sustained main-thread load idle callbacks can starve indefinitely,
  // which would silently drop an interval flush. The timeout forces the callback to run anyway.
  if (typeof ric === "function") ric(cb, { timeout: 2000 });
  else setTimeout(cb, 0);
}
